import { ArrowLeft, Loader2, MailCheck } from "lucide-react"
import * as React from "react"
import { Link } from "react-router"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"

import { AuthLayout } from "./auth-layout"
import { useForgotPasswordMutation, type ForgotPasswordError } from "./auth-store"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function ForgotPasswordPage(): JSX.Element {
  const forgot = useForgotPasswordMutation()

  const [email, setEmail] = React.useState("")
  const [attempted, setAttempted] = React.useState(false)
  const [submittedEmail, setSubmittedEmail] = React.useState<string | null>(null)

  const emailError = React.useMemo<string | null>(() => {
    if (!attempted) return null
    const trimmed = email.trim()
    if (trimmed.length === 0) return "Enter your email address."
    if (!EMAIL_RE.test(trimmed)) return "That doesn't look like an email."
    return null
  }, [attempted, email])

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setAttempted(true)
    const trimmed = email.trim()
    if (trimmed.length === 0 || !EMAIL_RE.test(trimmed)) return
    if (forgot.isPending) return
    try {
      await forgot.mutateAsync(trimmed)
      setSubmittedEmail(trimmed)
    } catch {
      /* surfaced via forgot.error */
    }
  }

  const topError = useTopError(forgot.error)

  if (submittedEmail) {
    return (
      <AuthLayout
        eyebrow="Forgot password"
        title="Check your email"
        subtitle="If that address has an account, a reset link is on its way."
        footer={
          <p>
            <Link to="/login" className="text-brand hover:underline">
              Back to sign in
            </Link>
          </p>
        }
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-surface-2 p-4">
            <div className="flex items-start gap-3">
              <MailCheck className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden="true" />
              <div className="min-w-0 space-y-1 text-body-sm text-text-2">
                <p>
                  We sent a link to <span className="font-medium text-text">{submittedEmail}</span>.
                </p>
                <p>
                  Open it on this device to choose a new password. The link expires shortly and can
                  only be used once.
                </p>
              </div>
            </div>
          </div>

          <p className="text-caption text-text-3">
            Didn't get anything? Check your spam folder, or wait a minute before trying again — we
            throttle repeat requests to protect your account.
          </p>

          <Button
            type="button"
            variant="ghost"
            size="md"
            block
            onClick={() => {
              setSubmittedEmail(null)
              setAttempted(false)
              forgot.reset()
            }}
          >
            Use a different email
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      eyebrow="Forgot password"
      title="Forgot your password?"
      subtitle="Enter the email associated with your account and we'll send you a link to choose a new one."
      footer={
        <p>
          <Link to="/login" className="inline-flex items-center gap-1 text-text-2 hover:text-text">
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Back to sign in
          </Link>
        </p>
      }
    >
      <form
        onSubmit={onSubmit}
        className="space-y-4"
        noValidate
        aria-busy={forgot.isPending || undefined}
      >
        <div className="sr-only" aria-live="polite" role="status">
          {forgot.isPending ? "Sending reset link…" : topError ? topError.body : ""}
        </div>

        {topError && (
          <Alert tone="error" size="sm" title={topError.title}>
            {topError.body}
          </Alert>
        )}

        <FormField label="Email" required error={emailError}>
          <Input
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@company.com"
            inputSize="lg"
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value)
              if (forgot.error) forgot.reset()
            }}
            disabled={forgot.isPending}
            autoFocus
          />
        </FormField>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          leadingIcon={forgot.isPending ? <Loader2 className="animate-spin" /> : undefined}
          disabled={forgot.isPending}
        >
          {forgot.isPending ? "Sending…" : "Send reset link"}
        </Button>
      </form>
    </AuthLayout>
  )
}

interface TopError {
  title: string
  body: string
}

function useTopError(error: ForgotPasswordError | null): TopError | null {
  const [remaining, setRemaining] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!error || error.retryAfterSeconds == null) {
      setRemaining(null)
      return
    }
    setRemaining(error.retryAfterSeconds)
    const t = setInterval(() => {
      setRemaining((prev) => {
        if (prev == null) return null
        if (prev <= 1) {
          clearInterval(t)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, [error])

  if (!error) return null
  switch (error.kind) {
    case "rate_limited":
      return {
        title: "Too many attempts",
        body: remaining && remaining > 0 ? `Try again in ${remaining}s.` : "You can try again now.",
      }
    case "network":
      return {
        title: "Network problem",
        body: "We couldn't reach the server. Check your connection and try again.",
      }
    case "server":
      return {
        title: "Server error",
        body: "Something went wrong on our side. Please try again in a moment.",
      }
    default:
      return { title: "Couldn't send reset link", body: error.message }
  }
}
